import React, { useEffect, useState } from "react";
import {
  Modal,
  View,
  ScrollView,
  Text,
  TouchableOpacity,
  Image,
  Keyboard,
} from "react-native";
import { AppFontStyle } from "../../../../styles/AppFontStyle";
import { AppColors } from "../../../../utility/AppColors";
import {
  CreateResponsiveStyle,
  DEVICE_SIZES,
  useDeviceSize,
} from "rn-responsive-styles";
import { toast } from "react-toastify";
import { ShowLoader } from "../../../../common/ShowLoader";
import { AppImages } from "../../../../utility/AppImages";
import ConnectUserList from "../../../../Components/connect-user-common-components/ConnectUserList";
import CommonTextInputWeb from "../../../../common/CommonTextInputWeb";
import { useChat } from "../../../../context/ChatContext";
import { searchUsers } from "../../../../services/eventsAPI";
import { AddMemberInChat } from "../../../../services/chatAPI";

type AddMemberContactModalProps = {
  isVisible: boolean;
  onClose: () => void;
  otherMembers: string[];
};

const DEBOUNCE_DELAY = 150;

const AddMemberContactModal: React.FC<AddMemberContactModalProps> = ({
  isVisible,
  onClose,
  otherMembers = [],
}) => {
  const { currentChat, userDataRef, setChatData } = useChat();
  const styles = useStyles();
  const deviceSize = useDeviceSize();
  const [allUsers, setAllUsers] = useState([]);
  const [showLoader, setShowLoader] = useState(false);
  const [search, setSearch] = useState("");
  const [searchData, setSearchData] = useState([]);
  const [arrListData, setArrListData] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [isSearchPerformed, setIsSearchPerformed] = useState(false);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, DEBOUNCE_DELAY);

    return () => clearTimeout(handler);
  }, [search]);

  useEffect(() => {
    if (debouncedSearch.length > 0) {
      setIsSearchPerformed(true);
      callSearchAPI({
        bodyParams: {
          query: debouncedSearch,
          currentUsername: userDataRef.current.username,
          otherMembers: otherMembers,
        },
        responseSuccess: (response) => {
          if (response) {
            const users = response.results.map((item, index) => ({
              id: index,
              userID: item.userID,
              profilePicture: item.profilePicture,
              isSelected: 0,
            }));
            setAllUsers(users);
            setSearchData(users);
          }
        },
      });
    } else {
      setSearchData(allUsers);
    }
  }, [debouncedSearch]);

  useEffect(() => {
    let grouped = searchData.reduce((acc, obj) => {
      let firstChar = obj.userID.charAt(0).toUpperCase();
      if (!acc[firstChar]) {
        acc[firstChar] = [];
      }
      obj.isSelected = selectedUsers.some((u) => u.userID === obj.userID)
        ? 1
        : 0;
      acc[firstChar].push(obj);
      return acc;
    }, {});

    let result = Object.keys(grouped).map((key) => ({
      firstChar: key,
      users: grouped[key],
    }));

    const sortedData = result.sort((a, b) => {
      if (a.firstChar < b.firstChar) return -1;
      if (a.firstChar > b.firstChar) return 1;
      return 0;
    });

    setArrListData(sortedData);
  }, [searchData, selectedUsers]);

  const callSearchAPI = async ({ bodyParams, responseSuccess }) => {
    setShowLoader(true);
    try {
      const data = await searchUsers(bodyParams);
      setShowLoader(false);

      if (data.statusCode === 200) {
        responseSuccess(data.body);
      } else {
        toast.error(data.body.error || "An error occurred");
      }
    } catch (error) {
      setShowLoader(false);
      toast.error(error.message);
    }
  };

  const updateArrSelectionValue = (index: number, subIndex: number) => {
    let arr = [...arrListData];
    const selectedUser = arr[index].users[subIndex];

    selectedUser.isSelected = selectedUser.isSelected === 1 ? 0 : 1;

    let updatedSelectedUsers = selectedUser.isSelected
      ? [...selectedUsers, selectedUser]
      : selectedUsers.filter((u) => u.userID !== selectedUser.userID);

    setSelectedUsers(updatedSelectedUsers);
    setArrListData(arr);
  };

  const handleAddMembers = async () => {
    if (selectedUsers.length === 0) return;

    const selectedUserIDs = selectedUsers.map((user) => user.userID);

    try {
      setShowLoader(true);

      const response = await AddMemberInChat(
        userDataRef.current.accessToken,
        currentChat.conversationId,
        selectedUserIDs
      );

      setShowLoader(false);

      if (response?.statusCode === 200) {
        const updatedMembers = [...currentChat.members, ...selectedUserIDs];

        //@ts-ignore
        setChatData((prevChatData) =>
          prevChatData.map((chat) =>
            chat.conversationId === currentChat.conversationId
              ? { ...chat, members: updatedMembers }
              : chat
          )
        );

        setSelectedUsers([]); // Clear selected users
        setSearch(""); // Clear the search input
        toast.success("Members added successfully");
        onClose();
      } else if (response?.statusCode === 500) {
        const errorBody = response?.body ? JSON.parse(response.body) : {};
        const errorMessage = errorBody.error || "An error occurred";
        toast.error(errorMessage);
        onClose();
      } else {
        toast.error(response?.message || "Failed to add members");
      }
    } catch (error) {
      setShowLoader(false);
      console.error("Error adding members:", error);
      toast.error("An error occurred while adding members");
    }
  };

  return (
    <Modal transparent={true} visible={isVisible}>
      <View style={styles.container}>
        <View
          style={
            deviceSize === DEVICE_SIZES.XS
              ? styles.mobileContainer
              : styles.container
          }
        >
          <ScrollView
            contentContainerStyle={styles.mainView}
            showsVerticalScrollIndicator={false}
          >
            <View style={styles.header}>
              <Text style={[AppFontStyle.BOLD_ANYBODY_20, styles.headerText]}>
                Add Members
              </Text>
              <TouchableOpacity
                onPress={handleAddMembers}
                style={[
                  styles.addButton,
                  { opacity: selectedUsers.length > 0 ? 1 : 0.5 },
                ]}
                disabled={selectedUsers.length === 0}
              >
                <Text
                  style={[AppFontStyle.BOLD_ANYBODY_16, styles.addButtonText]}
                >
                  Add
                </Text>
              </TouchableOpacity>
              <TouchableOpacity onPress={onClose} style={styles.closeButton}>
                <Image
                  source={AppImages.icCancel}
                  style={styles.closeIcon}
                  resizeMode="contain"
                />
              </TouchableOpacity>
            </View>
            <View
              style={{
                borderColor: AppColors.white_D6DDEB,
                borderWidth: 1,
                borderRadius: 10,
                marginTop: 15,
                marginRight: 5,
                width: 246,
                height: 32,
                justifyContent: "center",
              }}
            >
              <CommonTextInputWeb
                placeholder="Search Name"
                mainStyle={[styles.textInputStyle]}
                inputStyle={[
                  AppFontStyle.REGULAR_ANYBODY_14,
                  { paddingVertical: 0, paddingHorizontal: 0 },
                ]}
                keyboardType="default"
                value={search}
                onChangeText={setSearch}
                prefixIconVisibility={true}
                onSubmitEditing={() => {
                  Keyboard.dismiss();
                }}
                autoCapitalize="none"
                isPassword={false}
                prefixIcon={AppImages.icSearchGray}
                iconStyle={{ width: 24, height: 24 }}
              />
            </View>
            <View style={styles.listContainer}>
              {arrListData.length === 0 && !isSearchPerformed ? (
                <Text style={styles.noUserText}>Search Users to Add</Text>
              ) : arrListData.length === 0 && isSearchPerformed ? (
                <Text style={styles.noUserText}>No such user found</Text>
              ) : (
                <ConnectUserList
                  data={arrListData}
                  onSelect={updateArrSelectionValue}
                />
              )}
            </View>
          </ScrollView>
        </View>
        {showLoader && <ShowLoader showIndicator={showLoader} />}
      </View>
    </Modal>
  );
};

const useStyles = CreateResponsiveStyle(
  {
    container: {
      flex: 1,
      backgroundColor: AppColors.blackTrans_000000a8,
      justifyContent: "center",
      alignItems: "center",
    },
    mobileContainer: {
      width: "85%",
      marginTop: 20,
      flex: 1,
      backgroundColor: AppColors.blackTrans_000000a8,
    },
    mainView: {
      backgroundColor: AppColors.black_121212,
      borderColor: AppColors.grayColor_282828,
      borderRadius: 10,
      borderWidth: 1,
      padding: 20,
      width: 370,
      height: "94%",
      paddingHorizontal: 15,
      marginHorizontal: 30,
      marginVertical: "5%",
      alignSelf: "center",
    },
    header: {
      flexDirection: "row",
      paddingTop: 20,
      columnGap: 50,
    },
    headerText: {
      paddingBottom: 5,
      alignSelf: "center",
      flex: 1,
    },
    addButton: {
      backgroundColor: AppColors.green_32CD32,
      borderRadius: 5,
      paddingTop: 8,
      paddingBottom: 4,
      paddingHorizontal: 10,
    },
    addButtonText: {
      color: AppColors.white,
    },
    closeButton: {
      borderWidth: 1.8,
      borderColor: AppColors.grayColor_393939,
      borderRadius: 10,
      height: 30,
      width: 30,
    },
    closeIcon: {
      height: 12,
      width: 12,
      alignSelf: "center",
      marginVertical: 7,
    },
    listContainer: {
      flex: 1,
      paddingVertical: 20,
      paddingHorizontal: 10,
    },
    noUserText: {
      color: AppColors.white,
      fontSize: 16,
      textAlign: "center",
      marginTop: 20,
    },
    textInputStyle: {
      borderWidth: 0,
      paddingHorizontal: 10,
    },
  },
  {
    [DEVICE_SIZES.MD]: {
      textInputStyle: {
        width: "100%",
        marginRight: 0,
      },
    },
    [DEVICE_SIZES.XS]: {
      mainView: {
        width: "100%",
        padding: 12,
        marginHorizontal: 0,
        marginVertical: 0,
      },
      listContainer: {
        paddingVertical: 10,
        paddingHorizontal: 5,
      },
      textInputStyle: {
        marginRight: 0,
      },
    },
  }
);

export default AddMemberContactModal;
